import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import clsx from "clsx";
import React, { createContext, useContext, useCallback, useMemo } from "react";
import { AlertIcon } from "../Icons/Icons";
const CheckboxContext = createContext(null);
const useCheckboxContext = () => {
    const context = useContext(CheckboxContext);
    if (!context) {
        throw new Error("Checkbox must be used within a CheckboxGroup");
    }
    return context;
};
export const CheckboxGroup = ({ name, label, children, description, error, disabled = false, required = false, value, defaultValue = [], onChange, className = "", }) => {
    const isSingleCheckbox = React.Children.count(children) === 1;
    const [selectedValues, setSelectedValues] = React.useState(value ?? defaultValue);
    React.useEffect(() => {
        if (value !== undefined) {
            setSelectedValues(value);
        }
    }, [value]);
    const updateSelected = useCallback((checkboxValue) => {
        setSelectedValues((prev) => {
            const newValues = prev.includes(checkboxValue)
                ? prev.filter((v) => v !== checkboxValue)
                : [...prev, checkboxValue];
            if (onChange) {
                if (isSingleCheckbox) {
                    onChange(newValues[0] === true);
                }
                else {
                    onChange(newValues);
                }
            }
            return newValues;
        });
    }, [onChange, isSingleCheckbox]);
    const contextValue = useMemo(() => ({
        selectedValues,
        updateSelected,
        isDisabled: disabled,
        name,
        error,
    }), [selectedValues, updateSelected, disabled, name, error]);
    return (_jsxs("fieldset", { className: clsx("min-w-0", className), disabled: disabled, "aria-required": required, "aria-invalid": !!error, children: [label && (_jsxs("legend", { className: "text-base font-normal mb-4", children: [label, required && _jsx("span", { className: "text-red-600 ml-1", children: "*" })] })), _jsx("div", { className: "space-y-2", children: _jsx(CheckboxContext.Provider, { value: contextValue, children: children }) }), description && (_jsx("p", { className: "mt-2 text-sm text-gray-600", children: description })), error && (_jsxs("div", { className: "flex items-center gap-1 mt-2 text-red-600", children: [_jsx(AlertIcon, {}), _jsx("span", { className: "text-sm", children: error })] }))] }));
};
export const Checkbox = ({ value, label, disabled = false, className = "", description, ...props }) => {
    const { selectedValues, updateSelected, isDisabled, error } = useCheckboxContext();
    const isChecked = selectedValues.includes(value);
    const isDisabledFinal = disabled || isDisabled;
    const handleClick = () => {
        if (!isDisabledFinal) {
            updateSelected(value);
        }
    };
    return (_jsxs("div", { role: "checkbox", "aria-checked": isChecked, "aria-disabled": isDisabledFinal, onClick: handleClick, className: clsx("relative w-full border rounded-lg transition-all duration-200", "hover:cursor-pointer group", {
            "border-red-600": error,
            "border-gray-300": !error,
            "bg-gray-100": isDisabledFinal,
            "opacity-60": isDisabledFinal,
        }, className), ...props, children: [_jsx("div", { className: clsx("absolute inset-0 rounded-lg opacity-0 transition-opacity duration-200", "bg-idk-gradient", "group-hover:opacity-100", { hidden: isDisabledFinal }) }), _jsxs("div", { className: "relative flex items-start p-4 z-10", children: [_jsx("div", { className: "flex-shrink-0 w-4 h-4 mt-1", children: isChecked ? (_jsxs("svg", { role: "presentation", xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 16 16", children: [_jsx("path", { d: "M15 0H1C0.447715 0 0 0.447715 0 1V15C0 15.5523 0.447715 16 1 16H15C15.5523 16 16 15.5523 16 15V1C16 0.447715 15.5523 0 15 0Z", fill: "#1c2735" }), _jsx("path", { d: "M15 0H1C0.45 0 0 0.45 0 1V15C0 15.55 0.45 16 1 16H15C15.55 16 16 15.55 16 15V1C16 0.45 15.55 0 15 0Z", fill: "#1c2735" }), _jsx("path", { d: "M8.00005 12C7.80005 12 7.61005 11.94 7.45005 11.83L4.45005 9.82998C3.99005 9.51998 3.87005 8.89998 4.17005 8.43998C4.47005 7.97998 5.10005 7.85998 5.56005 8.15998L7.62005 9.52998L10.1101 4.53998C10.3601 4.04998 10.9601 3.84998 11.4501 4.08998C11.9401 4.33998 12.1401 4.93998 11.9001 5.42998L8.90005 11.43C8.77005 11.69 8.54005 11.88 8.26005 11.95C8.18005 11.97 8.09005 11.98 8.00005 11.98V12Z", fill: "white" })] })) : (_jsx("svg", { role: "presentation", xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 16 16", children: _jsx("path", { d: "M14 2V14H2V2H14ZM15 0H1C0.45 0 0 0.45 0 1V15C0 15.55 0.45 16 1 16H15C15.55 16 16 15.55 16 15V1C16 0.45 15.55 0 15 0Z", fill: "#d5dde7" }) })) }), _jsxs("div", { className: "ml-3", children: [_jsx("div", { className: "block text-base font-normal", children: label }), description && (_jsx("div", { className: "block mt-1 text-sm text-gray-600", children: description }))] })] })] }));
};
//# sourceMappingURL=Checkbox.js.map